'use client';

import Image, { type ImageProps } from 'next/image';

import {
  type ImageResizeOptions,
  buildResizeUrl,
} from '@ps/functions/utils/image-loader';
import {
  blurhashFromUrl,
  cleanImageUrl,
  imageMetadataFromUrl,
} from '@ps/functions/utils/image-metadata';

import { useIsHydrated } from '../hooks/useIsHydrated';
import { cn } from '../utils';
import { blurhashToDataUrl } from '../utils/blurhashToDataUrl';

export type ImageResizeMode =
  | 'cover'
  | 'contain'
  | 'stretch'
  | 'repeat'
  | 'center';

/**
 * Tailwind `object-fit` class for each resize mode. `stretch` ignores aspect
 * ratio (`object-fill`); `center` shows the image at natural size
 * (`object-none`, centred by the default `object-position`). `object-fit` can't
 * tile an `<img>`, so `repeat` also falls back to natural size.
 *
 * Applied as a CLASS (merged via `cn`/tailwind-merge) rather than an inline
 * `style.objectFit` so a caller's own `className="object-…"` can override it —
 * an inline style would always win and silently ignore the caller's class.
 */
const RESIZE_MODE_OBJECT_FIT_CLASS: Record<ImageResizeMode, string> = {
  cover: 'object-cover',
  contain: 'object-contain',
  stretch: 'object-fill',
  center: 'object-none',
  repeat: 'object-none',
};

export interface PopImageProps extends Omit<
  ImageProps,
  'placeholder' | 'blurDataURL' | 'loader' | 'unoptimized'
> {
  /**
   * Resize through our CDN resize service. When set, `src` is rewritten UP FRONT
   * to a single resize-service URL (`buildResizeUrl`) — no next/image srcset —
   * and also seeds the layout `width`/`height` when the caller leaves them unset.
   */
  resizing?: ImageResizeOptions;
  /** How the image fills its box, mapped to `object-fit`. Defaults to `cover`. */
  resizeMode?: ImageResizeMode;
  /**
   * BlurHash for the loading placeholder. Defaults to the URL fragment our GCP
   * uploads embed; decoded into next/image's `blurDataURL`.
   */
  blurhash?: string;
  /**
   * Opt in to Next's built-in optimizer (`/_next/image` — on-demand, server-side
   * srcset generation). Honored ONLY when `resizing` is unset AND the cleaned URL
   * is optimizable by Next (no query string, which its optimizer rejects). In
   * every other case the image renders unoptimized (a plain `<img>`). Defaults to
   * `false`: our remote images are served as-is, or via the resize CDN when
   * `resizing` is set — never transformed on our own app server.
   */
  optimized?: boolean;
}

/** A cleaned URL Next's built-in optimizer can handle — it rejects query strings. */
function isNextOptimizable(src: string | ImageProps['src']): boolean {
  if (typeof src !== 'string') return true;
  try {
    return new URL(src).search.length === 0;
  } catch {
    return !src.includes('?');
  }
}

/** A small, aspect-correct decode box for the blur placeholder. */
function blurDecodeSize(width?: number, height?: number): [number, number] {
  if (!width || !height) return [32, 32];
  return [32, Math.max(8, Math.round((32 * height) / width))];
}

/**
 * `next/image` for our remote (GCP / resize-service) images. Renders `unoptimized`
 * by default — a plain `<img>`, not Next's on-demand `/_next/image` optimizer
 * (which would transform images on our own app server, and rejects our URLs'
 * query strings). PopImage does the work itself, up front:
 *
 * - **Resize** — when `resizing` is set, `src` is rewritten to a SINGLE CDN
 *   resize-service URL (`buildResizeUrl`); no `srcset`, no next/image pipeline.
 *   The already-sized image is fetched straight from the resize CDN.
 * - **`optimized`** — opt back into Next's optimizer, honored ONLY when `resizing`
 *   is unset AND the cleaned URL is optimizable (no query string). Every other
 *   combination stays unoptimized.
 * - **Dimensions** — the embedded `_m` metadata (or the `resizing` target) seeds
 *   `width`/`height` when the caller leaves them unset (correct aspect, no shift).
 * - **BlurHash placeholder** — decoded from the URL fragment (needs a canvas, so
 *   after hydration — hence a client component; atoms are normally server-only)
 *   into next/image's `blurDataURL`.
 * - **`resizeMode`** mapped to `object-fit` (defaults to `cover`).
 */
export function PopImage({
  src,
  resizing,
  resizeMode = 'cover',
  blurhash,
  optimized = false,
  style,
  className,
  width,
  height,
  fill,
  ...rest
}: PopImageProps) {
  const isHydrated = useIsHydrated();

  const isStringSrc = typeof src === 'string';
  const metadata = isStringSrc ? imageMetadataFromUrl(src) : undefined;
  const hash = blurhash ?? (isStringSrc ? blurhashFromUrl(src) : undefined);

  // Final src: the cleaned URL, rewritten to a single resize-service URL when
  // `resizing` is set. Resolved here (not via a next/image loader) so next
  // renders a plain <img> — no srcset, no on-demand optimization on our server.
  const cleanSrc = isStringSrc ? cleanImageUrl(src) : src;
  const resolvedSrc =
    resizing != null && typeof cleanSrc === 'string'
      ? buildResizeUrl(cleanSrc, resizing)
      : cleanSrc;

  // Let Next generate images ONLY when the caller opts in, there's no explicit
  // resize, and the cleaned URL is actually optimizable by Next. Everything else
  // renders unoptimized.
  const letNextOptimize =
    optimized && resizing == null && isNextOptimizable(cleanSrc);

  // Seed layout dimensions from the embedded metadata (or the resize target)
  // when the caller didn't size the image — unless it's a `fill` image.
  const fallbackWidth = metadata?.width ?? resizing?.width;
  const fallbackHeight = metadata?.height ?? resizing?.height;
  const resolvedWidth = width ?? (fill ? undefined : fallbackWidth);
  const resolvedHeight = height ?? (fill ? undefined : fallbackHeight);

  const [blurWidth, blurHeight] = blurDecodeSize(
    metadata?.width,
    metadata?.height,
  );
  const blurDataURL = isHydrated
    ? blurhashToDataUrl(hash, blurWidth, blurHeight)
    : undefined;
  const blurProps: Pick<ImageProps, 'placeholder' | 'blurDataURL'> | undefined =
    blurDataURL ? { placeholder: 'blur', blurDataURL } : undefined;

  return (
    <Image
      src={resolvedSrc}
      unoptimized={!letNextOptimize}
      width={resolvedWidth}
      height={resolvedHeight}
      fill={fill}
      // `resizeMode`'s object-fit is a CLASS so a caller's own `className`
      // (e.g. `object-contain`) can override it via tailwind-merge — an inline
      // style would always win. Defaults to `object-cover`.
      className={cn(RESIZE_MODE_OBJECT_FIT_CLASS[resizeMode], className)}
      style={style}
      {...blurProps}
      {...rest}
    />
  );
}
